import React, { useEffect, useState } from "react";
import "./Categories.scss";

import { FaPlus } from "react-icons/fa6";
import { FaPen, FaTrash } from "react-icons/fa";
import { IoSearch } from "react-icons/io5";
import { IoIosArrowBack } from "react-icons/io";

import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import ROUTES from "../../constants/routes";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import {
  removeCategory,
  updateCategories,
} from "../../redux/features/organization-slice";
import {
  deleteCategory,
  fetchCategoriesByOrganization,
} from "../../api/categories";
import { toast } from "react-toastify";
import AddCategory from "./AddCategory/AddCategory";
import { MdAddBox } from "react-icons/md";
import useUserIsAllowed from "../../helpers/userAuthorized";
import { storage } from "../../helpers/storage";
import SyncLoader from "../../components/SyncLoader/SyncLoader";
import { updateNetworkStatus } from "../../redux/features/home-slice";

const Categories = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { id: organization_id, categories: orgCategories } = useSelector(
    (state) => state.organizationReducer.value
  );

  const [deleting, setDeleting] = useState(false);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [backgroundLoadingFailed, setBackgroundLoadingFailed] = useState(false);

  const [openDelete, setOpenDelete] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState("");

  const [categories, setCategories] = useState([]);
  const [selectedDesktopTag, setSelectedDesktopTag] = useState("Add Category");

  const userIsAdministrator = useUserIsAllowed("administrator");
  const userIsAllowedToManageInventory = useUserIsAllowed("manage_inventory");

  const userIsAllowedToDeleteCategory =
    userIsAdministrator || userIsAllowedToManageInventory;

  const backgroundLoadCategories = () => {
    setBackgroundLoading(true);
    fetchCategoriesByOrganization(organization_id, "")
      .then((res) => {
        if (res.data) {
          const categories = res.data;
          storage.set("categories", JSON.stringify(categories));
          dispatch(updateCategories({ categories }));
        }
        setBackgroundLoadingFailed(false);
      })
      .catch((err) => {
        setBackgroundLoadingFailed(true);
      })
      .finally(() => {
        setBackgroundLoading(false);
      });
  };

  useEffect(() => {
    if (organization_id) {
      backgroundLoadCategories();
    }
  }, [organization_id]);

  const getAllCategories = () => {
    if (searchKeyword) {
      const regex = new RegExp(searchKeyword, "i");
      const filteredCategories = orgCategories.filter((category) =>
        regex.test(category?.name)
      );
      if (filteredCategories?.length < 1) {
        toast.dismiss();
        toast.error(`No categories found for ${searchKeyword}.`);
      }
      setCategories(filteredCategories);
    } else {
      setCategories(orgCategories);
    }
    setSelectedCategory(null);
    setSelectedDesktopTag("Add Category");
  };

  const handleDelete = () => {
    if (selectedCategory?.id) {
      setDeleting(true);
      deleteCategory(selectedCategory?.id)
        .then((res) => {
          dispatch(removeCategory({ category: selectedCategory }));
          toast.dismiss();
          toast.success("Category deleted successfully.");
          setOpenDelete(false);
        })
        .catch((err) => {
          toast.dismiss();
          toast.error("Error while deleting category!");
        })
        .finally(() => {
          setDeleting(false);
        });
    }
  };

  useEffect(() => {
    getAllCategories();
  }, [searchKeyword, orgCategories]);

  useEffect(() => {
    if (backgroundLoading) {
      dispatch(updateNetworkStatus({ status: "syncing" }));
    }
  }, [backgroundLoading]);

  return (
    <>
      <div className="page-with-side-components">
        <div className="categories-page">
          <div className="desktop-navbar box-shadow">
            <p>{ROUTES.categories.title}</p>
            <div
              className="last-tag-svg"
              onClick={() => {
                setSelectedCategory(null);
                setSelectedDesktopTag("Add Category");
              }}
            >
              <FaPlus />
            </div>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setSearchKeyword(e.target.keyword.value);
            }}
            className="categories-search"
          >
            <div className="inputs">
              <input
                defaultValue={searchKeyword}
                type="text"
                name="keyword"
                placeholder="Search"
              />
              <button type="submit">
                <IoSearch />
              </button>
            </div>
          </form>

          <div
            className={`container-wrapper categories-list ${
              categories?.length > 0 ? "grid" : ""
            }`}
          >
            {categories?.map((el, i) => {
              return (
                <div
                  key={i}
                  className={`item ${
                    selectedCategory?.id === el?.id
                      ? "highlight-selected-item"
                      : ""
                  }`}
                >
                  <div className="left">
                    <p>{el?.name}</p>
                  </div>

                  <div className="right">
                    <Link
                      to={ROUTES.editCategory.path}
                      state={{ editData: el }}
                    >
                      <FaPen className="mobile" />
                    </Link>
                    <FaPen
                      className="desktop"
                      onClick={() => {
                        setSelectedCategory(el);
                        setSelectedDesktopTag("Edit Category");
                      }}
                    />
                    <FaTrash
                      onClick={() => {
                        if (userIsAllowedToDeleteCategory) {
                          setSelectedCategory(el);
                          setOpenDelete(true);
                        } else {
                          toast.dismiss();
                          toast.info(
                            "You do not have sufficient permissions to perform this action. Please contact your administrator."
                          );
                        }
                      }}
                      className="danger"
                    />
                  </div>
                </div>
              );
            })}

            {categories?.length === 0 && (
              <div className="no-item">
                <Link className="mobile" to={ROUTES.addCategory.path}>
                  <MdAddBox />
                </Link>
                <Link
                  className="desktop"
                  to={"#"}
                  onClick={() => {
                    setSelectedDesktopTag("Add Category");
                  }}
                >
                  <MdAddBox />
                </Link>
                <p>Hey! Let's add your first category!</p>
              </div>
            )}
          </div>

          <div className="open-add-icon">
            <FaPlus
              onClick={() => {
                navigate(ROUTES.addCategory.path);
              }}
            />
          </div>

          <DeleteModal
            deleting={deleting}
            handleDelete={handleDelete}
            name={`${selectedCategory?.name} Category`}
            open={openDelete}
            setOpen={setOpenDelete}
          />
        </div>
        <div className="right-desktop-view">
          <div className="desktop-navbar border-bottom box-shadow">
            {selectedDesktopTag === "Edit Category" && (
              <div
                className="back"
                onClick={() => {
                  setSelectedDesktopTag("Add Category");
                  setSelectedCategory(null);
                }}
              >
                <IoIosArrowBack />
              </div>
            )}
            <p>{selectedDesktopTag}</p>
          </div>

          {selectedDesktopTag === "Add Category" && (
            <AddCategory desktopView={true} />
          )}
          {selectedDesktopTag === "Edit Category" && (
            <AddCategory
              isEdit={true}
              selected={selectedCategory}
              desktopView={true}
              setSelectedDesktopTag={setSelectedDesktopTag}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Categories;
